//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import React, { ReactElement, useEffect } from 'react'

import { Box, Button, Typography } from '@mui/material'

import { EthAddr } from '@shared/SchemaTypes'

import { useOtChainState, useOtDispatch, useOtSelector } from '@react/redux/store'

import { erc20WeiToCommaDecString } from '@shared/conversions'
import { PoolOverviewCTO, PoolOverviewCTOSchema } from '@shared/sharedTypes'

import { AddressPill } from '@react/components/addresses/AddressPill'
import ViewField from '@react/components/elements/ViewField'
import { theme } from '@react/components/theme'

import { fetchErc20BalanceForPool } from '@react/fetchers/PoolFetchers'
import { completeRolloverNetPaymentTX } from '@react/signedTxs/completeRolloverNetPaymentTX'
import { ETH_ADDR_ZERO } from '@shared/SchemaTypes'
import { logOT } from '@shared/loggingShared'

export default function AdminCompleteRolloverApproveWithdrawsForm(props: { poolAddr: EthAddr }): ReactElement {
  const { poolAddr } = props
  const dispatch = useOtDispatch()
  const {
    chainConfiguration: { providerReadOnly },
  } = useOtChainState()
  const [vaultBalance, setVaultBalance] = React.useState<bigint>(0n)
  const poolOverviewCTO = useOtSelector(
    (state) => state.poolState.poolOverviewCTOs[poolAddr ?? ETH_ADDR_ZERO] as PoolOverviewCTO
  )

  const [isSubmitted, setIsSubmitted] = React.useState(false)
  const sx = {
    backgroundColor: theme.palette.fg.main,
    color: '#FFFFFF',
    padding: '11px 16px 11px 16px',
    borderRadius: '6px',
    marginTop: '8px',
  }

  useEffect(() => {
    if (poolOverviewCTO && providerReadOnly) {
      fetchErc20BalanceForPool(providerReadOnly, poolOverviewCTO.borrowerVaultAddr, poolAddr)
        .then((result) => {
          setVaultBalance(result)
        })
        .catch((error) => {
          logOT.error(error)
        })
    }
  }, [poolOverviewCTO, providerReadOnly])

  const totalRepayment =
    Number(poolOverviewCTO.maturedLoanState.assetsToReturnToPool) +
    Number(poolOverviewCTO.maturedLoanState.originationFee)

  // if (totalRepayment <= vaultBalance) {
  //   const event = new Event('modalClose')
  //   window.dispatchEvent(event)
  // }

  const completeRolloverNetPaymentAction = (): void => {
    dispatch(completeRolloverNetPaymentTX({ poolAddr }))
    setIsSubmitted(true)
    const event = new Event('modalClose')
    window.dispatchEvent(event)
  }

  const RedeemRequestList = (): React.ReactElement => {
    return (
      <Box sx={{ marginBottom: 4, marginTop: 8 }}>
        <Typography variant="bodyBold" sx={{ marginBottom: 4 }}>
          Redemption Requests
        </Typography>
        {poolOverviewCTO.redemptionLenders.map((lenderAddr: EthAddr, index: number) => {
          const requestedShares = Number(poolOverviewCTO.requestedShares[index])
          const redeemableShares = poolOverviewCTO.redeemableShares[index]
          if (requestedShares > 0) {
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginBottom: 2,
                  marginTop: 2,
                  alignItems: 'center',
                }}
              >
                <AddressPill addr={lenderAddr} />
                <Typography>
                  {' '}
                  needs approval for {erc20WeiToCommaDecString(requestedShares)}
                  {poolOverviewCTO.symbol} redemption
                </Typography>
              </div>
            )
          } else {
            return (
              <div
                key={index}
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  marginBottom: 2,
                  marginTop: 2,
                  alignItems: 'center',
                }}
              >
                <AddressPill addr={lenderAddr} />
                <Typography>
                  {' '}
                  is approved for {erc20WeiToCommaDecString(redeemableShares)}
                  {poolOverviewCTO.symbol} redemption
                </Typography>
              </div>
            )
          }
        })}
      </Box>
    )
  }

  const FormField = ({ label, value }: { label: string; value: any }): React.ReactElement => (
    <Box sx={{ marginBottom: 4, marginTop: 4 }}>
      <Typography variant="bodyMuted" gutterBottom component="div">
        {label}
      </Typography>
      <Typography variant="bodyBold" gutterBottom component="div">
        {value}
      </Typography>
    </Box>
  )

  return (
    <Box data-testid="POOL_CREATE_FORM" style={{ paddingTop: 20 }}>
      <Typography variant="body1" sx={{ marginTop: 8 }}>
        This will result in the Assets to Return to Vault (including Fees) amount being transferred from the Borrower
        Vault to the Loan Vault where it can be withdrawn by the Lender(s)
      </Typography>

      <Box sx={{ marginTop: 8 }}>
        <ViewField
          dataObject={poolOverviewCTO}
          fieldSchema={PoolOverviewCTOSchema.describe().fields.rolloverMaturingLoanFees}
        />
      </Box>

      <Box sx={{ marginTop: 4 }}>
        <FormField
          label="Assets to Return to Vault (including Fees)"
          value={`${erc20WeiToCommaDecString(totalRepayment)} ${poolOverviewCTO.liquidityTokenSymbol}`}
        />
      </Box>

      <FormField
        label="Borrower Vault Balance"
        value={`${erc20WeiToCommaDecString(vaultBalance)} ${poolOverviewCTO.liquidityTokenSymbol}`}
      />

      <RedeemRequestList />

      <Button
        disabled={isSubmitted}
        sx={sx}
        onClick={() => {
          completeRolloverNetPaymentAction()
        }}
        variant={'contained'}
      >
        <Typography variant={'buttonBold'}>Complete Rollover</Typography>
      </Button>
    </Box>
  )
}
